// regras/reposicoes.js — o que acontece quando uma aula NAO acontece.
//
// "Nao houve" sao tres perguntas, nao uma:
//   (a) QUEM causou      → o motivo
//   (b) e o DINHEIRO?    → consome credito do pacote, ou nao
//   (c) e a AGENDA?      → nada, direito a reposicao, ou desconto quando repor e' inviavel
//
// Este modulo responde (b) e (c) a partir de (a) + as regras do contrato. Nao toca no
// banco: recebe os dados, devolve a decisao. A tela e a voz usam a mesma funcao.

// Motivos que a pessoa escolhe na tela (U21). O motivo e' para a relacao e a estatistica;
// quem decide o dinheiro continua sendo QUEM cancelou e QUANDO. Feriado e' a excecao:
// nao e' culpa de ninguem, a aula e' empurrada — nao consome nem vira reposicao.
export const MOTIVOS_HUMANOS = [
  { id: 'agenda',   rotulo: 'Problema de agenda / compromisso', icone: 'calendar' },
  { id: 'doente',   rotulo: 'Estou doente',                      icone: 'first-aid-kit' },
  { id: 'viagem',   rotulo: 'Viagem',                            icone: 'plane' },
  { id: 'feriado',  rotulo: 'Feriado',                           icone: 'confetti' },
  { id: 'clima',    rotulo: 'Chuva ou local fechado',            icone: 'cloud-rain' },
  { id: 'outro',    rotulo: 'Outro motivo',                      icone: 'dots' },
];
// 'pausa' nao e' escolha da pessoa (nao aparece no seletor): e' o motivo que o banco grava nas
// aulas canceladas por uma pausa aceita (pausar_pacote). So' o rotulo, para as telas (v131).
export const ROTULO_MOTIVO = { ...Object.fromEntries(MOTIVOS_HUMANOS.map(m => [m.id, m.rotulo])), pausa: 'Pausa' };

// Categoria de regra a partir do motivo humano + quem cancelou + se avisou no prazo.
export function categoriaDoMotivo({ motivoHumano, quem, noPrazo = true }) {
  if (motivoHumano === 'clima') return 'forca_maior';
  if (quem === 'professor') return 'professor';
  return noPrazo ? 'aluno_no_prazo' : 'aluno_fora_prazo';
}

export const MOTIVOS = {
  aluno_fora_prazo: 'Aluno furou ou desmarcou em cima da hora',
  aluno_no_prazo:   'Aluno avisou dentro do prazo',
  professor:        'Eu que desmarquei',
  forca_maior:      'Chuva, quadra fechada, feriado',
};

// O status gravado na aula continua sendo o que o resto do app ja' entende.
export const STATUS_POR_MOTIVO = {
  aluno_fora_prazo: 'falta',
  aluno_no_prazo:   'cancelada_aluno',
  professor:        'cancelada_professor',
  forca_maior:      'cancelada_forca_maior',
};

export const MOTIVO_POR_STATUS = Object.fromEntries(
  Object.entries(STATUS_POR_MOTIVO).map(([m, s]) => [s, m])
);

// Regras padrao quando o contrato (ou o professor) nao definiu.
export const REGRAS_PADRAO = {
  reposicao_permitida: true,        // aluno que avisa no prazo tem direito a repor (pacote)
  reposicao_validade_dias: 30,      // prazo para usar a reposicao
  forca_maior_gera: 'reposicao',    // 'reposicao' | 'desconto' | 'nada'
  forca_maior_teto_mes: 1,          // no maximo N reposicoes por forca maior por mes
  reposicao_inviavel: 'expira',     // quando venceu sem usar: 'expira' | 'desconto'
};

function regra(contrato, campo) {
  const v = contrato ? contrato[campo] : undefined;
  return v === undefined || v === null ? REGRAS_PADRAO[campo] : v;
}

// Valor de uma aula deste contrato — e' o que vira desconto quando repor e' inviavel.
export function valorAula(contrato) {
  if (!contrato) return 0;
  if (contrato.tipo_cobranca === 'mensal') {
    const porSemana = Number(contrato.aulas_por_semana) || 1;
    return Math.round((Number(contrato.valor_mensal) || 0) / (porSemana * 4.33) * 100) / 100;
  }
  const total = Number(contrato.total_aulas) || 0;
  return total > 0 ? Math.round((Number(contrato.preco_negociado) || 0) / total * 100) / 100 : 0;
}

/**
 * Decide o desfecho de uma aula que nao aconteceu.
 * @param {object} p
 * @param {string} p.motivo            uma das chaves de MOTIVOS
 * @param {object} p.contrato          linha de contratos (tipo_cobranca, reposicao_permitida, ...)
 * @param {number} p.forcaMaiorNoMes   quantas reposicoes por forca maior este aluno ja' ganhou no mes
 * @param {string} [p.hoje]            'YYYY-MM-DD' (para teste)
 */
export function decidir({ motivo, contrato, forcaMaiorNoMes = 0, hoje }) {
  const mensal = contrato?.tipo_cobranca === 'mensal';
  const status = STATUS_POR_MOTIVO[motivo];
  if (!status) throw new Error('motivo desconhecido: ' + motivo);

  const base = {
    motivo, status,
    consome: false,            // desconta 1 aula do pacote?
    gera_reposicao: false,     // nasce um direito a reposicao?
    gera_desconto: false,      // vira desconto direto (sem tentar repor)?
    validade_dias: Number(regra(contrato, 'reposicao_validade_dias')) || 30,
    valor_credito: valorAula(contrato),
    bloqueado_por_teto: false,
    explicacao: '',
  };

  // ---- pacote ---------------------------------------------------------------
  if (!mensal) {
    if (motivo === 'aluno_fora_prazo') {
      return { ...base, consome: true, explicacao: 'Consome 1 aula do pacote — a regra que ele aceitou no contrato.' };
    }
    if (motivo === 'aluno_no_prazo') {
      const pode = !!regra(contrato, 'reposicao_permitida');
      return { ...base, gera_reposicao: pode,
        explicacao: pode ? `Não consome. Ele tem ${base.validade_dias} dias para repor.` : 'Não consome. Este contrato não prevê reposição.' };
    }
    if (motivo === 'professor') {
      return { ...base, gera_reposicao: true, explicacao: 'Não consome. Você deve uma reposição a ele.' };
    }
    // forca maior
    return decidirForcaMaior(base, contrato, forcaMaiorNoMes);
  }

  // ---- mensal ---------------------------------------------------------------
  // A mensalidade corre tendo treinado ou nao. Reposicao aqui e' uma aula-credito
  // com validade — NUNCA empurra a data da mensalidade (o caixa precisa ser previsivel).
  // Decisao do Marcus (21/09): mensalidade fixa e' fixa para os dois lados. Nem o aluno
  // nem o professor geram credito — e isso esta' escrito no acordo que os dois confirmam.
  if (motivo === 'aluno_fora_prazo' || motivo === 'aluno_no_prazo') {
    return { ...base, explicacao: 'A mensalidade corre. Sem crédito.' };
  }
  if (motivo === 'professor') {
    return { ...base, explicacao: 'Mensalidade fixa: não gera reposição nem desconto. Se quiser, combine uma aula extra por cortesia.' };
  }
  return { ...base, explicacao: 'Mensalidade fixa: não gera reposição nem desconto.' };
}

function decidirForcaMaior(base, contrato, jaNoMes) {
  const gera = regra(contrato, 'forca_maior_gera');
  const teto = Number(regra(contrato, 'forca_maior_teto_mes'));
  if (gera === 'nada') {
    return { ...base, explicacao: 'Não consome. Este contrato não repõe por chuva ou imprevisto.' };
  }
  if (teto > 0 && jaNoMes >= teto) {
    return { ...base, bloqueado_por_teto: true,
      explicacao: `Não consome, mas o limite de ${teto} reposição${teto > 1 ? 'ões' : ''} por imprevisto no mês já foi usado.` };
  }
  if (gera === 'desconto') {
    return { ...base, gera_desconto: true,
      explicacao: `Não consome. Vira desconto de R$ ${base.valor_credito.toFixed(0)} na próxima cobrança.` };
  }
  return { ...base, gera_reposicao: true,
    explicacao: `Não consome. Nasce uma reposição a combinar — ${base.validade_dias} dias para usar.` };
}

// Data-limite de uma reposicao criada hoje.
export function validadeDe(decisao, hoje = new Date()) {
  const d = new Date(hoje);
  d.setDate(d.getDate() + (decisao.validade_dias || 30));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// v148 (chuva) — "a chuva e' problema dos dois" (Marcus, 21/10). Nas modalidades AO AR LIVRE, cancelar por chuva
// ate' o INICIO da aula nao conta para ninguem, sem limite no mes: no pacote a aula vai para o fim do pacote; na
// mensalidade vira reposicao a combinar. Quem decide e' o banco (public._chuva_livre / _ao_ar_livre); estas duas
// funcoes so' servem para a TELA dizer a regra antes de perguntar ao banco. A lista e' a MESMA do banco
// (sql/v148_chuva.sql, _ao_ar_livre): mudar os dois juntos.
// Fecho v148: tambem os outros nomes das mesmas (bike, bicicleta, MTB, running, footvolley, volei de/na areia/praia,
// beach volei) e NAO quando o nome diz que e' coberto (indoor, esteira, ergometrica, quadra coberta).
export function aoArLivre(modalidade) {
  const t = ' ' + String(modalidade || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/[^a-z]+/g, ' ') + ' ';
  return (/ (beach ?tenn?is|futevolei|futvolei|footvolley|corrida|running|ciclismo|pedal|bike|bicicleta|mtb|ar livre) /.test(t)
    || / volei( de| na)? (praia|areia) /.test(t)
    || / beach ?vol(ei|ley) /.test(t)
    || (/ tenn?is /.test(t) && !/ tenn?is de mesa /.test(t) && !/ table tennis /.test(t)))
    && !/ (indoor|esteira|ergometric[a-z]*|ergometro|coberta|coberto) /.test(t);
}

// Ao ar livre pela modalidade da ficha; ficha sem modalidade: pelas do professor, so' quando TODAS sao ao ar livre
// (com uma so', e' ela). A mesma conta do banco (_chuva_livre).
export function chuvaPelaModalidade(modalidade, modalidadesProf = []) {
  const m = String(modalidade || '').trim();
  if (m) return aoArLivre(m);
  const mods = Array.isArray(modalidadesProf) ? modalidadesProf : [];
  return mods.length > 0 && mods.every(x => aoArLivre(x));
}

// O acordo segue a regra da chuva? contrato.chuva_livre: true/false = o professor escolheu; null = automatico pela
// modalidade (chuvaPelaModalidade). Turma: nao. Sem a coluna (banco sem a v148): nao — o banco ainda trata a chuva
// como imprevisto comum.
export function chuvaLivre(contrato, modalidade, modalidadesProf = []) {
  if (!contrato || contrato.turma_id || !('chuva_livre' in contrato)) return false;
  if (contrato.chuva_livre === true || contrato.chuva_livre === false) return contrato.chuva_livre;
  return chuvaPelaModalidade(modalidade, modalidadesProf);
}
